// ============================================================================
// AdminTeamManagePage — gestion d'une equipe (GEST-ADMIN-IA volet 3)
// ============================================================================
// Flux suivable : en-tete + directeur assignable, liste membres (retirer),
// ajout par recherche. Modele UX ExecutiveGrantsTab (liste + ajouter/retirer).
// Mutations existantes uniquement : PUT /teams/:id (directeur), PUT /members/:email
// (teamId) — aucune regle d'acces touchee (STOP-8).

import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Loader2, Pencil, Plus, Trash2, Users } from 'lucide-react';
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  GsBreadcrumb,
  GsSearchInput,
  GsSelect,
  QueryError,
  SkeletonCard,
  GS_COMPONENTS,
  type Column,
} from '@gs/ui';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import {
  useAdminTeams,
  useUpdateTeam,
  adminTeamErrorCode,
  type AdminTeamSummary,
  type TeamUpdatePayload,
} from '../../hooks/useAdminTeams';
import {
  useAdminMembers,
  useUpdateMember,
  adminMemberErrorCode,
  type AdminMember,
  type MemberUpdatePayload,
} from '../../hooks/useAdminMembers';
import { SectionHeader } from '../../components/admin/SectionHeader';
import { TeamFormDialog, type TeamFormValues } from '../../components/admin/TeamFormDialog';

const norm = (s: string): string => s.toLowerCase().trim();

function memberUpdatePayload(member: AdminMember, teamId: string | null): MemberUpdatePayload {
  return {
    fullName: member.fullName,
    matricule: member.matricule,
    teamId,
    committee: member.committee === 'ce' || member.committee === 'cg' ? member.committee : null,
    managerEmail: member.managerEmail,
    orgLevel: member.orgLevel,
  };
}

function initialValuesFor(team: AdminTeamSummary): TeamFormValues {
  return {
    name: team.name,
    department: team.department ?? '',
    headcount: String(team.headcount),
    directorEmail: team.directorEmail ?? '',
  };
}

export default function AdminTeamManagePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const teamsQ = useAdminTeams(!!user);
  const membersQ = useAdminMembers(!!user);
  const updateTeam = useUpdateTeam();
  const updateMember = useUpdateMember();

  const [editOpen, setEditOpen] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [removeTarget, setRemoveTarget] = useState<AdminMember | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const team = useMemo(
    () => teamsQ.data?.teams.find((tm) => tm.id === id),
    [teamsQ.data, id],
  );

  const teamMembers = useMemo(
    () => (membersQ.data?.members ?? []).filter((m) => m.teamId === id),
    [membersQ.data, id],
  );

  const addCandidates = useMemo(() => {
    if (!membersQ.data || !id) return [];
    const q = norm(addSearch);
    return membersQ.data.members
      .filter((m) => m.teamId !== id && m.isEmployed)
      .filter((m) => !q || norm(`${m.fullName ?? ''} ${m.email} ${m.matricule ?? ''}`).includes(q))
      .slice(0, 8);
  }, [membersQ.data, id, addSearch]);

  const pending = updateTeam.isPending || updateMember.isPending;

  if (teamsQ.isError && teamsQ.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  const isLoading = teamsQ.isLoading || membersQ.isLoading;
  const isError = teamsQ.isError || membersQ.isError;
  const notFound = !isLoading && !isError && teamsQ.data && id && !team;

  const directorOptions = (teamsQ.data?.assignable ?? []).map((p) => ({
    value: p.email,
    label: p.fullName ?? p.email,
  }));
  if (team?.directorEmail && !directorOptions.some((o) => o.value === team.directorEmail)) {
    directorOptions.push({
      value: team.directorEmail,
      label: team.directorName ?? team.directorEmail,
    });
  }

  function onDirectorChange(directorEmail: string) {
    if (!team) return;
    setErrorCode(null);
    updateTeam.mutate(
      {
        id: team.id,
        payload: {
          name: team.name,
          department: team.department,
          headcount: team.headcount,
          directorEmail: directorEmail ? directorEmail : null,
        },
      },
      { onError: (err) => setErrorCode(adminTeamErrorCode(err)) },
    );
  }

  function onAddMember(member: AdminMember) {
    if (!id) return;
    setErrorCode(null);
    updateMember.mutate(
      { email: member.email, payload: memberUpdatePayload(member, id) },
      {
        onSuccess: () => setAddSearch(''),
        onError: (err) => setErrorCode(adminMemberErrorCode(err)),
      },
    );
  }

  function confirmRemove() {
    if (!removeTarget) return;
    setErrorCode(null);
    updateMember.mutate(
      { email: removeTarget.email, payload: memberUpdatePayload(removeTarget, null) },
      {
        onSuccess: () => setRemoveTarget(null),
        onError: (err) => {
          setErrorCode(adminMemberErrorCode(err));
          setRemoveTarget(null);
        },
      },
    );
  }

  function submitTeamMeta(payload: TeamUpdatePayload) {
    if (!team) return;
    setErrorCode(null);
    updateTeam.mutate(
      { id: team.id, payload },
      {
        onSuccess: () => setEditOpen(false),
        onError: (err) => {
          setEditOpen(false);
          setErrorCode(adminTeamErrorCode(err));
        },
      },
    );
  }

  const memberColumns: Column<AdminMember>[] = [
    {
      key: 'name',
      header: t('gest:admin.teams.manage.columns.name'),
      render: (m) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-slate-900 dark:text-slate-100">{m.fullName ?? m.email}</p>
          <p className="truncate text-meta text-slate-500 dark:text-slate-400">{m.email}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('gest:admin.teams.manage.columns.status'),
      render: (m) => (
        <span className={m.isEmployed ? 'text-green-700 dark:text-green-400' : 'text-slate-500'}>
          {m.isEmployed
            ? t('gest:admin.teams.manage.statusActive')
            : t('gest:admin.teams.manage.statusInactive')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (m) => (
        <button
          type="button"
          disabled={pending}
          onClick={() => setRemoveTarget(m)}
          className={`${GS_COMPONENTS.btnSecondary} touch-manipulation inline-flex items-center gap-1.5`}
        >
          <Trash2 size={14} aria-hidden="true" />
          {t('gest:admin.teams.manage.remove')}
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <GsBreadcrumb
        ariaLabel={t('gest:admin.teams.manage.breadcrumbLabel')}
        LinkComponent={Link}
        items={[
          { label: t('gest:admin.title'), to: '/admin?tab=teams' },
          { label: team?.name ?? '…' },
        ]}
      />

      {/* Le bouton « Retour » qui vivait ici DOUBLAIT le fil pose deux lignes plus
          haut : meme destination (/admin?tab=teams), deux affordances, dont une
          hors du repere de navigation (E-20260901-gest-nav-retour-etiquette-bouton).
          Le fil suffit — c'est le standard 1121. */}

      {errorCode ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">
          {t(`gest:admin.teams.errors.${errorCode}`, {
            defaultValue: t(`gest:admin.members.errors.${errorCode}`, { defaultValue: errorCode }),
          })}
        </p>
      ) : null}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : isError ? (
        <QueryError
          message={t('gest:admin.teams.manage.loadError')}
          onRetry={() => {
            void teamsQ.refetch();
            void membersQ.refetch();
          }}
        />
      ) : notFound ? (
        <EmptyState
          icon={<Users size={48} aria-hidden="true" />}
          title={t('gest:admin.teams.manage.notFoundTitle')}
          description={t('gest:admin.teams.manage.notFoundDescription')}
          action={
            <Link to="/admin?tab=teams" className={GS_COMPONENTS.btnSecondary}>
              {t('gest:admin.teams.manage.back')}
            </Link>
          }
        />
      ) : team && teamsQ.data ? (
        <>
          <section className={`${GS_COMPONENTS.card} flex flex-col gap-4 p-5`}>
            <SectionHeader title={team.name} description={team.department ?? undefined}>
              <span className="text-chip text-slate-400 dark:text-slate-500">
                {t('gest:admin.teams.manage.meta', {
                  members: teamMembers.length,
                  headcount: team.headcount || '—',
                })}
              </span>
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5`}
              >
                <Pencil size={14} aria-hidden="true" />
                {t('gest:admin.teams.manage.editMeta')}
              </button>
            </SectionHeader>

            <div className="flex max-w-md flex-col gap-1.5">
              <span className="text-meta font-medium text-slate-600 dark:text-slate-300">
                {t('gest:admin.teams.form.director')}
              </span>
              <GsSelect
                value={team.directorEmail ?? ''}
                onChange={onDirectorChange}
                options={directorOptions}
                placeholder={t('gest:admin.teams.form.directorNone')}
                ariaLabel={t('gest:admin.teams.form.director')}
                disabled={pending}
              />
              <p className="text-meta text-slate-400 dark:text-slate-500">
                {t('gest:admin.teams.form.directorHint')}
              </p>
            </div>
          </section>

          <section className="flex flex-col gap-4">
            <SectionHeader
              title={t('gest:admin.teams.manage.membersTitle')}
              description={t('gest:admin.teams.manage.membersDescription')}
            >
              <span className="text-chip text-slate-400 dark:text-slate-500">
                {t('gest:admin.teams.manage.membersCount', { n: teamMembers.length })}
              </span>
            </SectionHeader>

            {teamMembers.length === 0 ? (
              <EmptyState
                icon={<Users size={48} aria-hidden="true" />}
                title={t('gest:admin.teams.manage.emptyMembers')}
                description={t('gest:admin.teams.manage.emptyMembersHint')}
              />
            ) : (
              <DataTable rowKey={(m) => m.email} columns={memberColumns} data={teamMembers} />
            )}
          </section>

          <section className={`${GS_COMPONENTS.card} flex flex-col gap-3 p-4`}>
            <h3 className="text-body font-semibold text-slate-900 dark:text-slate-100">
              {t('gest:admin.teams.manage.addTitle')}
            </h3>
            <p className="text-meta text-slate-500 dark:text-slate-400">
              {t('gest:admin.teams.manage.addDescription')}
            </p>
            <GsSearchInput
              value={addSearch}
              onChange={setAddSearch}
              placeholder={t('gest:admin.teams.manage.addPlaceholder')}
              className="w-full sm:max-w-md"
            />
            {addCandidates.length === 0 ? (
              <p className="text-field text-slate-500 dark:text-slate-400">
                {t('gest:admin.teams.manage.noCandidates')}
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {addCandidates.map((m) => (
                  <li
                    key={m.email}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-slate-800 dark:text-slate-100">
                        {m.fullName ?? m.email}
                      </span>
                      <span className="block truncate text-meta text-slate-500 dark:text-slate-400">{m.email}</span>
                    </span>
                    <button
                      type="button"
                      disabled={pending}
                      onClick={() => onAddMember(m)}
                      className={`${GS_COMPONENTS.btnAction} touch-manipulation inline-flex items-center gap-1.5`}
                    >
                      {updateMember.isPending ? (
                        <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                      ) : (
                        <Plus size={14} aria-hidden="true" />
                      )}
                      {t('gest:admin.teams.manage.add')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <TeamFormDialog
            open={editOpen}
            mode="edit"
            initial={initialValuesFor(team)}
            assignable={teamsQ.data.assignable}
            isPending={updateTeam.isPending}
            onClose={() => setEditOpen(false)}
            onSubmit={submitTeamMeta}
          />

          <ConfirmDialog
            open={removeTarget !== null}
            variant="danger"
            title={t('gest:admin.teams.manage.removeConfirmTitle')}
            message={t('gest:admin.teams.manage.removeConfirmBody', {
              name: removeTarget?.fullName ?? removeTarget?.email ?? '',
            })}
            confirmLabel={t('gest:admin.teams.manage.remove')}
            cancelLabel={t('gest:admin.teams.manage.removeConfirmCancel')}
            isLoading={updateMember.isPending}
            onConfirm={confirmRemove}
            onCancel={() => setRemoveTarget(null)}
          />
        </>
      ) : null}
    </div>
  );
}
